import { forOwn } from 'lodash';
import { DataTexture, RGBAFormat, UnsignedByteType } from 'three';
import { PointCloudContentState } from '../../../../../state/point-cloud-content-state';
import { CloudShaderMaterial } from '../../../cloud-shader-material';

export interface SensorModeUniforms {
  highlightedSensor: number;
}

export const sensorModeUniforms = `
  struct SensorModeUniforms {
    int highlightedSensor;
  };
`;

export const getColorForSensorMode = `
  uniform SensorModeUniforms sensorModeUniforms;
  uniform sampler2D pointColorBySensor;

  // sourceId is float because of two ThreeJS r147 limitations
  // 1. when using 'defaultAttributeValues' ThreeJS cast values to floats
  // using gl.vertexAttrib[1234]fv
  // 2. when sending Uint8 or Uint16 to the GPU they are casted to floats
  attribute float sourceId;

  bool isSensorHighlighted() {
    int highlightedSensor = sensorModeUniforms.highlightedSensor;

    return (highlightedSensor >= 0 && highlightedSensor == int(sourceId));
  }

  vec4 getPointColorForSourceId() {
    vec3 rgb = texelFetch(pointColorBySensor, ivec2(int(sourceId), 0), 0).rgb;
    return vec4(rgb, 1.0);
  }

  bool isSensorVisible() {
    return texelFetch(pointColorBySensor, ivec2(int(sourceId), 0), 0).a > 0.0;
  }

  vec4 getColorForSensorMode() {
    int highlightedSensor = sensorModeUniforms.highlightedSensor;

    if(!isSensorHighlighted() && !isSensorVisible()) {
      return vec4(0.0, 0.0, 0.0, 0.0);
    }

    if(highlightedSensor == -1 || isSensorHighlighted()) {
      return getPointColorForSourceId();
    } else {
      return vec4(0.478, 0.478, 0.478, 1.0);
    }
  }
`;

export function setSensorModeUniforms(
  material: CloudShaderMaterial,
  state?: PointCloudContentState,
) {
  let highlightedSensor: number;
  let texture: DataTexture | undefined;

  if (state) {
    highlightedSensor = state.render.layerSettings.highlightedSensor;
    texture = buildTexture(state);
  } else {
    highlightedSensor = -1;
    texture = undefined;
  }

  material.defaultAttributeValues.sourceId = [0];
  material.setColorUniform('sensor', { highlightedSensor });
  material.setUniform('pointColorBySensor', texture);
}

function buildTexture(state: PointCloudContentState) {
  const { pointAppearanceBySensor } = state.render.layerSettings;
  const pointColorBySensor = new Uint8Array(255 * 4);

  forOwn(pointAppearanceBySensor, (rgba, sourceId) => {
    pointColorBySensor.set(rgba, +sourceId * 4);
  });

  return new DataTexture(
    pointColorBySensor,
    Math.max(...Object.keys(pointAppearanceBySensor).map(Number)) + 1,
    1,
    RGBAFormat,
    UnsignedByteType,
  );
}
